/**
 * v2 API 클라이언트
 *
 * 잔류물질안전정보시스템 v3의 모든 API 호출에 사용되는 Axios 인스턴스.
 * - baseURL: /api/v2
 * - 요청 인터셉터: X-Requested-With 헤더 추가
 * - 응답 인터셉터: success 필드 기반 응답 언래핑
 * - 에러 인터셉터: 에러 로깅 (401 시 자동 모달 표시는 비활성화 — 권한관리 구현 시 활성화)
 */
import axios from 'axios';

const v2Client = axios.create({
  baseURL: '/api/v2',
  timeout: 30000,
  withCredentials: true,
});

// ─── 요청 인터셉터 ─────────────────────────────────────────

v2Client.interceptors.request.use(
  (config) => {
    // AJAX 요청임을 서버에 알리기 위한 헤더 추가
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    // 개발용 역할 토글 제거 — 실제 세션 로그인 사용
    // (구) localStorage 'buj-dev-role' 헤더 전송 비활성화
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ─── 응답 인터셉터 ─────────────────────────────────────────

v2Client.interceptors.response.use(
  (response) => {
    // 서버 응답에 success 필드가 있으면 data만 추출하여 반환 (응답 언래핑)
    if (response.data?.success !== undefined) {
      return response.data;
    }
    return response;
  },
  (error) => {
    // 에러 로깅 (401 포함 모든 에러)
    const { config, response } = error;
    const status = response?.status;

    if (status === 401) {
      console.warn(
        `[v2Client] 401 인증 필요: ${config?.method?.toUpperCase()} ${config?.url}`
      );
      // 로그인 모달 자동 표시 (순환 참조 방지 — lazy import)
      // /auth/* 엔드포인트 요청이나 로그인 체크(fetchUserInfo) 요청은 제외
      const url = config?.url || '';
      const isAuthEndpoint = url.startsWith('/auth') || url.includes('/get_userinfo');
      if (!isAuthEndpoint) {
        Promise.all([
          import('../store'),
          import('../store/authSlice'),
        ]).then(([storeMod, sliceMod]) => {
          const store = storeMod.default || storeMod.store;
          if (store && sliceMod.showLoginModal) {
            store.dispatch(sliceMod.showLoginModal());
          }
        }).catch(() => { /* ignore */ });
      }
    } else {
      console.error(
        `[v2Client] API 오류: ${config?.method?.toUpperCase()} ${config?.url}`,
        status,
        response?.data || error.message
      );
    }

    return Promise.reject(error);
  }
);

export default v2Client;
